/**
 * Organise step of the unified import flow — period / site / tags, then upload.
 *
 * Writes the shared form fields (periodMethod, siteMethod, timePeriodYear,
 * timePeriodMonthIndex, siteUuid, tags) on the React Hook Form context provided
 * by `ImportLayout`. "Begin upload" validates those fields then opens the
 * spreadsheet importer and submits the assembled FormData to the mixed-import
 * endpoint — there is no separate upload step. Reggie infers input_mode (pack
 * vs total weight) per row.
 *
 * Staff can tick "Import via the new catalogue pipeline" on purchases to send
 * the same rows to the catalogue pipeline's endpoint instead, while it is
 * verified before replacing the mixed-import upload.
 */
import useAuth from '@app/auth/use-auth';
import { useImport } from '@app/components/imports/import-layout';
import { getGoodsImportRoutes } from '@app/components/imports/goods-import/goods-import-routes';
import { useFormattedSites } from '@app/ccf/company-report/utils';
import Page from '@app/components/layout/page/page';
import MultiTagsPicker from '@app/components/multi-tag-picker';
import { useGetSites } from '@shared/api';
import { useCreatePurchasedGoodsImport } from '@shared/api/hooks/ccf/imports';
import { useProcessMixedImport } from '@shared/api/hooks/ccf/mixed-imports';
import { OperationReportTypeEnum } from '@shared/api/types';
import { useGetValidReportingPeriods } from '@shared/api/hooks/reports/periods';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import CheckboxFormField from '@shared/components/form/checkbox-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import GuideTemplate from '@shared/components/guide-template';
import Loader from '@shared/components/loader';
import sentry from '@shared/services/sentry';
import {
  formatUKDateToISO,
  generateTimePeriodMonthOptions,
} from '@shared/utils/date-formatters';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ActivityType,
  parseNumberWithCommas,
  periodMethodOptions,
  siteMethodOptions,
} from '../../utils';
import {
  buildPurchasedGoodsImportRequest,
  summarisePurchasedGoodsImportError,
} from '../catalogue-import';
import CsvImporterGoods from '../components/csv-importer-goods';
import { GoodsImportData } from '../types';

export default function GoodsImportConfigurePage() {
  const { reportType } = useImport();
  const { setValue, handleSubmit, getValues, watch } = useFormContext();
  const navigate = useNavigate();
  const [importerOpen, setImporterOpen] = useState(false);

  const routes = getGoodsImportRoutes(reportType);
  const isSales = reportType === OperationReportTypeEnum.sales;

  const { user } = useAuth();
  const canUseCataloguePipeline = !!user?.isStaff && !isSales;
  const sendToCataloguePipeline =
    canUseCataloguePipeline && !!watch('viaCataloguePipeline');

  const { mutateAsync: processMixed, isLoading: isMixedUploading } =
    useProcessMixedImport(routes.goodsType);
  const {
    mutateAsync: createCatalogueImport,
    isLoading: isCatalogueUploading,
  } = useCreatePurchasedGoodsImport();
  const isUploading = isMixedUploading || isCatalogueUploading;

  const { data: reportingYears, isLoading: isLoadingReportingPeriods } =
    useGetValidReportingPeriods();

  const { data: sitesData, isLoading: isSitesLoading } = useGetSites({
    page: 1,
    page_size: 1000,
  });
  const { results: sites } = sitesData || { results: [] };
  const formattedSites = useFormattedSites(sites!);

  const periodMethod = watch('periodMethod');
  const siteMethod = watch('siteMethod');
  const timePeriodYear = watch('timePeriodYear');

  const excludeDate = periodMethod && periodMethod !== 'auto';
  const excludeSite = siteMethod && siteMethod !== 'auto';

  const timePeriodMonthOptions = useMemo(() => {
    const reportingYear = reportingYears?.find(
      (year) => year.name === timePeriodYear
    );
    return generateTimePeriodMonthOptions(reportingYear!);
  }, [timePeriodYear, reportingYears]);

  useEffect(() => {
    if (periodMethod === 'auto' && timePeriodYear) {
      setValue('timePeriodYear', undefined);
      setValue('timePeriodMonthIndex', undefined);
    }
  }, [periodMethod, setValue, timePeriodYear]);

  useEffect(() => {
    if (siteMethod === 'single' && formattedSites.length > 0) {
      setValue('siteUuid', '');
    }
    if (siteMethod === 'none' || siteMethod === 'auto') {
      setValue('siteUuid', undefined);
    }
  }, [formattedSites.length, setValue, siteMethod]);

  const handleUpload = async (rows: GoodsImportData, file: File) => {
    if (sendToCataloguePipeline) {
      const request = buildPurchasedGoodsImportRequest(rows, {
        activityTypes: (getValues('activityTypes') ?? []) as ActivityType[],
        timePeriodYear: getValues('timePeriodYear'),
        timePeriodMonthIndex: getValues('timePeriodMonthIndex'),
        siteUuid: getValues('siteUuid'),
        tags: getValues('tags'),
      });
      try {
        await createCatalogueImport({ data: request });
      } catch (error) {
        // The importer shows a thrown error's message and stays open for fixes.
        throw new Error(summarisePurchasedGoodsImportError(error));
      }
      navigate(routes.importsList);
      return;
    }

    try {
      const formData = new FormData();

      const timePeriodYearValue = getValues('timePeriodYear');
      if (timePeriodYearValue !== undefined) {
        formData.append('time_period_year', timePeriodYearValue);
      }
      const timePeriodMonthIndex = getValues('timePeriodMonthIndex');
      if (timePeriodMonthIndex !== undefined) {
        formData.append(
          'time_period_month_index',
          timePeriodMonthIndex.toString()
        );
      }
      const siteUuid = getValues('siteUuid');
      if (siteUuid !== undefined) {
        formData.append('site', siteUuid);
      }
      const tags = getValues('tags');
      if (tags !== undefined && tags.length > 0) {
        formData.append('tags', JSON.stringify(tags));
      }

      formData.append('file_name', file.name);
      formData.append('uploaded_file', file);

      const rowsData = rows.map((row) => ({
        name: row.name,
        input_quantity: row.quantity
          ? parseNumberWithCommas(row.quantity as string)
          : null,
        input_unit: row.unit ?? null,
        supplier: row.supplier ?? null,
        customer: row.customer ?? null,
        remote_id: row.remoteId ?? null,
        date: row.date ? formatUKDateToISO(row.date as string) : null,
        site: row.site ?? null,
        total_spend: row.totalSpend ?? null,
      }));
      formData.append('rows', JSON.stringify(rowsData));

      // Send the selected activity types; the backend decides how to handle
      // them (force a single type, treat unselected detections as "other", or
      // classify per-row).
      const activityTypes = (getValues('activityTypes') ??
        []) as ActivityType[];
      formData.append('activity_types', JSON.stringify(activityTypes));

      await processMixed(formData);

      navigate(routes.importsList);
    } catch (error) {
      sentry.log(error);
      throw error;
    }
  };

  return (
    <Page name="Import - Organise">
      <GuideTemplate
        className="w-full overflow-visible"
        preTitle={`Import ${isSales ? 'sales' : 'purchases'}`}
        title="Organise your data"
        description={
          <div className="space-y-4">
            <Text>
              We can provide breakdowns of your emissions by site and time
              period. If you have these as columns in your spreadsheet we can
              read them automatically — otherwise pick a single value to apply
              to the whole upload.
            </Text>
          </div>
        }
        primaryActionProps={{
          disabled: isLoadingReportingPeriods || isUploading,
          onClick: handleSubmit(() => setImporterOpen(true)),
          'data-testid': 'mixed-begin-upload',
        }}
        primaryActionText={isUploading ? 'Uploading…' : 'Begin upload'}
        secondaryActionText="Back"
        secondaryActionProps={{
          disabled: isLoadingReportingPeriods || isUploading,
          onClick: () => navigate(routes.types),
        }}
      >
        {reportingYears && (
          <form
            onSubmit={handleSubmit(() => setImporterOpen(true))}
            className="space-y-4 text-left"
          >
            <div>
              <SelectFormField
                name="periodMethod"
                label="How shall we assign a time period?"
                options={periodMethodOptions}
                className="w-[390px]"
                testId="period-method-select"
              />
              {periodMethod === 'single' && (
                <div className="mt-4 flex items-end space-x-2">
                  <SelectFormField
                    name="timePeriodYear"
                    label="Year"
                    options={
                      reportingYears?.map((year) => ({
                        value: year.name,
                        label: year.name,
                      })) || []
                    }
                    disabled={isLoadingReportingPeriods}
                    className="w-[120px]"
                  />
                  <SelectFormField
                    name="timePeriodMonthIndex"
                    label="Month"
                    optional
                    className="w-[260px]"
                    options={timePeriodMonthOptions}
                    disabled={isLoadingReportingPeriods || !timePeriodYear}
                    loading={isLoadingReportingPeriods}
                  />
                </div>
              )}
              {periodMethod === 'auto' && (
                <Text variant="subtle" className="mt-1">
                  You will need to select a column with UK format dates
                </Text>
              )}
            </div>
            <div className="mt-4">
              <SelectFormField
                name="siteMethod"
                label="How shall we assign a site?"
                options={siteMethodOptions}
                className="w-[390px]"
                disabled={formattedSites.length === 0}
                testId="site-method-select"
              />
              {formattedSites.length === 0 && (
                <Text className="mt-1">
                  <TextLink to="/sites">Add a site</TextLink> to organise your
                  data by location
                </Text>
              )}
              {siteMethod === 'single' && (
                <>
                  <Text variant="subtle" className="mt-1.5">
                    Select one of your sites below
                  </Text>
                  <div className="mt-1 flex items-end space-x-2">
                    <SelectSearchFormField
                      name="siteUuid"
                      pluralName="sites"
                      optional
                      className="w-full"
                      options={formattedSites}
                      disabled={isSitesLoading}
                      loading={isSitesLoading}
                    />
                  </div>
                </>
              )}
              {siteMethod === 'auto' && (
                <Text variant="subtle" className="mt-1">
                  Every row must match the name of one of your{' '}
                  <TextLink to="/sites">sites</TextLink>
                </Text>
              )}
            </div>
            <MultiTagsPicker
              name="tags"
              className="mt-4 w-[390px]"
              label="Add custom tags"
              allowCreate
              testId="tags-picker"
            />
            {canUseCataloguePipeline && (
              <CheckboxFormField
                name="viaCataloguePipeline"
                className="mt-4"
                label="Import via the new catalogue pipeline (staff only)"
              />
            )}
          </form>
        )}
        {isLoadingReportingPeriods && <Loader />}
        <CsvImporterGoods
          isOpen={importerOpen}
          onClose={() => setImporterOpen(false)}
          handleSubmit={handleUpload}
          excludeDate={!!excludeDate}
          excludeSite={!!excludeSite}
          // Sales imports carry a Customer instead of a Supplier.
          excludeSupplier={isSales}
          includeCustomer={isSales}
          // Total spend isn't fully supported for sold goods yet — hide it.
          excludeTotalSpend={isSales}
        />
      </GuideTemplate>
    </Page>
  );
}
